import { sendAnalytics } from '@audio-network/analytics'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { clientUpdateDownloadCount } from '@/client'
import {
	BulkDownloadSelection,
	clientDownloadTrack,
	clientDownloadTracks,
	clientGetDownloadOptions,
	DownloadOptions,
	getStemsDownloadSelection,
} from '@/client/downloads'
import logger from '@/client/utils/logger'
import {
	DOWNLOAD_LATENCY_PAGE_ACTION,
	recordDownloadLatency,
} from '@/components/app/NewRelic/recordDownloadLatency'
import useDownloadCentreStore, {
	hasInFlightDownloadCentreMessage,
	isInProgressDownloadCentreMessage,
	isTrackDownloading,
} from '@/stores/downloadCentreStore'
import sessionStore from '@/stores/sessionStore'
import { sendAlgoliaInsightEvents } from '@/utils/algolia/sendAlgoliaInsightEvents'
import { clientGetToggleFromCookie } from '@/utils/cookies/getToggleFromCookie'
import { createDownloadLink } from '@/utils/createDownloadLink'
import { createZipProgressPredictor } from '@/utils/createZipProgressPredictor'
import isBreakpoint from '@/utils/dom/isBreakpoint'
import { downloadBlobWithRangeResume } from '@/utils/downloadBlobWithRangeResume'
import { isAbortError } from '@/utils/isAbortError'
import { toast } from '@/utils/notifications/toast'
import { readDownloadResponseWithProgress } from '@/utils/readDownloadResponseWithProgress'

import {
	getBulkDownloadTracks,
	getMainTrackId,
	hasMixOptionsOnTracks,
	recordBulkDownloadCounts,
} from './DownloadOptions/DownloadOptions.utils'
import {
	BulkDownloadSelectionAnalytics,
	DownloadTracksOptions,
	UseDownloadOptions,
	UseDownloadTrack,
} from './DownloadOptionsModal.types'

const CLICKED = 'Clicked'
const STARTED = 'Started'
const COMPLETED = 'Completed'
const RETRIED = 'Retried'
const CANCELLED = 'Cancelled'
const SEARCH_TRACK_SOURCE = 'Search'
const BULK_DOWNLOAD_ADJECTIVE = 'All Track'
const STEMS_DOWNLOAD_ADJECTIVE = 'Stems'
const ZIP_PROGRESS_BAR_MAX = 50
const MEGABYTE_IN_BYTES = 1024 * 1024
export const MOBILE_DOWNLOAD_FAILED_TOAST = 'Download failed'

const toastMobileDownloadFailed = (): void => {
	if (!isBreakpoint('mobile')) {
		return
	}

	toast.error(MOBILE_DOWNLOAD_FAILED_TOAST)
}

const mapBulkFileDownloadProgress = (percentComplete: number): number =>
	ZIP_PROGRESS_BAR_MAX + (percentComplete / 100) * ZIP_PROGRESS_BAR_MAX

const formatDownloadSize = (downloadSizeInBytes: number): number =>
	parseFloat((downloadSizeInBytes / MEGABYTE_IN_BYTES).toFixed(2))

const getDownloadOptionsTrackIds = (tracks: Track[]): number[] => {
	return Array.from(new Set(tracks.map((track) => getMainTrackId(track))))
}

export const useDownloadOptions = (
	initialTracks: Track[]
): UseDownloadOptions => {
	const [downloadOptions, setDownloadOptions] =
		useState<DownloadOptions | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	const loadDownloadOptions = async (): Promise<void> => {
		if (isLoading) {
			return
		}

		if (!hasMixOptionsOnTracks(initialTracks)) {
			return
		}

		setIsLoading(true)

		try {
			const options = await clientGetDownloadOptions(
				getDownloadOptionsTrackIds(initialTracks)
			)
			setDownloadOptions(options)
		} finally {
			setIsLoading(false)
		}
	}

	return {
		downloadOptions,
		isLoading,
		loadDownloadOptions,
	}
}

export const useDownloadTrack = (
	trackSource: string,
	zipFileName: string,
	trackId?: number
): UseDownloadTrack => {
	const [associatedDownloadMessageId, setAssociatedDownloadMessageId] =
		useState<string | null>(null)
	const isDownloading = useDownloadCentreStore((state) =>
		trackId === undefined
			? hasInFlightDownloadCentreMessage(state.messages)
			: isTrackDownloading(state.messages, trackId)
	)
	const isAssociatedDownloadInProgress = useDownloadCentreStore((state) => {
		if (associatedDownloadMessageId === null) {
			return false
		}

		const associatedMessage = state.messages.find(
			(message) => message.id === associatedDownloadMessageId
		)

		return (
			associatedMessage !== undefined &&
			isInProgressDownloadCentreMessage(associatedMessage)
		)
	})
	const user = sessionStore((state) => state.session?.user)
	const refreshSession = sessionStore((state) => state.refreshSession)
	const { t } = useTranslation(['app'])

	const sendDownloadAnalytics = (
		pastTenseVerb: string,
		track: Track,
		fileType: string
	): void => {
		sendAnalytics({
			noun: 'Download',
			pastTenseVerb,
			eventParameters: {
				AlbumNo: track.album.number,
				Catalogue: track.album.catalogue.name,
				FileType: fileType,
				Genre: track.album.genre,
				TrackCount: 1,
				TrackId: track.id,
				TrackSource: trackSource,
				TrackTitle: track.title,
			},
		})
	}

	const sendStemsDownloadAnalytics = (
		pastTenseVerb: string,
		stemsTrack: Track,
		downloadSize?: number
	): void => {
		sendAnalytics({
			adjective: STEMS_DOWNLOAD_ADJECTIVE,
			noun: 'Download',
			pastTenseVerb,
			eventParameters: {
				Catalogue: stemsTrack.album.catalogue.name,
				Genre: stemsTrack.album.genre,
				TrackSource: trackSource,
				...(typeof downloadSize === 'number' && {
					DownloadSize: downloadSize,
				}),
			},
		})
	}

	const sendBulkDownloadAnalytics = (
		pastTenseVerb: string,
		selection: BulkDownloadSelection,
		fileType: string,
		downloadSize?: number,
		selectionAnalytics?: BulkDownloadSelectionAnalytics
	): void => {
		const downloadedTracks = getBulkDownloadTracks(selection)
		const firstTrack = downloadedTracks[0]

		sendAnalytics({
			adjective: BULK_DOWNLOAD_ADJECTIVE,
			noun: 'Download',
			pastTenseVerb,
			eventParameters: {
				FileType: fileType,
				TrackCount: downloadedTracks.length,
				TrackSource: trackSource,
				...(firstTrack?.album && {
					Catalogue: firstTrack.album.catalogue.name,
					Genre: firstTrack.album.genre,
				}),
				...(typeof downloadSize === 'number' && {
					DownloadSize: downloadSize,
				}),
				...selectionAnalytics,
			},
		})
	}

	const downloadTrack = async (
		track: Track,
		fileType: string,
		existingMessageId?: string
	): Promise<void> => {
		let messageId: string

		if (existingMessageId === undefined) {
			sendDownloadAnalytics(CLICKED, track, fileType)
			sendAlgoliaInsightEvents({
				eventName: 'Download Clicked',
				eventType:
					trackSource === SEARCH_TRACK_SOURCE
						? 'convertedObjectIDsAfterSearch'
						: 'convertedObjectIDs',
				track,
			})

			messageId = useDownloadCentreStore.getState().startDownload(
				() => {
					sendDownloadAnalytics(RETRIED, track, fileType)
					void downloadTrack(track, fileType, messageId)
				},
				{ trackId: track.id, trackTitle: track.title, type: 'single' }
			)
			setAssociatedDownloadMessageId(messageId)
		} else {
			messageId = existingMessageId
		}

		const downloadStartedAt = performance.now()

		try {
			const { fileName, url } = await clientDownloadTrack(track, fileType)

			sendDownloadAnalytics(STARTED, track, fileType)

			const fileResponse = await fetch(url, {
				headers: {
					Accept: 'application/octet-stream',
					'Content-Type': 'application/octet-stream',
				},
				method: 'GET',
			})

			if (!fileResponse.ok) {
				throw new Error('Download failed')
			}

			const { blob, fileName: downloadedFileName } =
				await readDownloadResponseWithProgress(
					fileResponse,
					(percent) =>
						useDownloadCentreStore
							.getState()
							.updateDownloadProgress(messageId, percent),
					fileName
				)

			await clientUpdateDownloadCount(
				track,
				fileType,
				user.id,
				user.organisation.id
			)

			createDownloadLink(blob, downloadedFileName)

			sendDownloadAnalytics(COMPLETED, track, fileType)
			useDownloadCentreStore
				.getState()
				.showSingleDownloadCompleted(messageId, track.title)

			recordDownloadLatency({
				downloadMode: 'single',
				durationMs: performance.now() - downloadStartedAt,
				fileType,
				outcome: 'success',
				pageActionName: DOWNLOAD_LATENCY_PAGE_ACTION,
				trackCount: 1,
			})

			if (user?.organisation.salesStatus === 'Trial') {
				void refreshSession()

				if (user.organisation.remainingDownloads === 1) {
					toast.success(t('trial-last-download-toast'), {
						'data-testid': 'last-download-toast',
					})
				}
			}
		} catch (error) {
			logger(
				'Error downloading',
				error instanceof Error ? error : new Error(String(error)),
				{
					errorType: 'Download',
					downloadMode: 'single',
				}
			)
			useDownloadCentreStore
				.getState()
				.showDownloadFailed(messageId, track.title)
			toastMobileDownloadFailed()
		}
	}

	const downloadTracks = async (
		selection: BulkDownloadSelection,
		fileType: string,
		options: DownloadTracksOptions = {}
	): Promise<void> => {
		const { existingMessageId, selectionAnalytics, stemsDownloadTrack } =
			options
		const abortController = new AbortController()
		const zipProgressPredictor = createZipProgressPredictor()
		let zipProgressTickId: number | undefined
		let messageId: string

		const sendTrackedDownloadAnalytics = (
			pastTenseVerb: string,
			downloadSize?: number
		): void => {
			if (stemsDownloadTrack) {
				sendStemsDownloadAnalytics(
					pastTenseVerb,
					stemsDownloadTrack,
					downloadSize
				)
				return
			}

			sendBulkDownloadAnalytics(
				pastTenseVerb,
				selection,
				fileType,
				downloadSize,
				selectionAnalytics
			)
		}

		const cancelBulkDownload = (): void => {
			sendTrackedDownloadAnalytics(CANCELLED)
			abortController.abort()
		}

		const stopPredictedZipProgressTicks = (): void => {
			if (zipProgressTickId === undefined) {
				return
			}

			window.clearInterval(zipProgressTickId)
			zipProgressTickId = undefined
		}

		if (existingMessageId === undefined) {
			sendTrackedDownloadAnalytics(CLICKED)

			messageId = useDownloadCentreStore.getState().startDownload(
				() => {
					sendTrackedDownloadAnalytics(RETRIED)
					void downloadTracks(selection, fileType, {
						existingMessageId: messageId,
						selectionAnalytics,
						stemsDownloadTrack,
					})
				},
				{
					abortDownload: cancelBulkDownload,
					trackTitle: zipFileName,
					type: 'bulk',
				}
			)
			setAssociatedDownloadMessageId(messageId)
		} else {
			messageId = existingMessageId
			useDownloadCentreStore
				.getState()
				.setAbortDownload(messageId, cancelBulkDownload)
		}

		const publishPredictedZipProgress = (): void => {
			useDownloadCentreStore
				.getState()
				.updateDownloadProgress(
					messageId,
					zipProgressPredictor.getPercentComplete()
				)
		}

		const startPredictedZipProgressTicks = (): void => {
			if (zipProgressTickId !== undefined) {
				return
			}

			zipProgressTickId = window.setInterval(publishPredictedZipProgress, 100)
		}

		const downloadStartedAt = performance.now()
		const latencyTrackCount = getBulkDownloadTracks(selection).length

		try {
			const { fileName, url } = await clientDownloadTracks(
				selection,
				fileType,
				zipFileName,
				(zipProgress) => {
					if (zipProgress.status === 'preparing') {
						zipProgressPredictor.beginPreparing(zipProgress.totalFiles)
						useDownloadCentreStore
							.getState()
							.beginBulkPreparing(messageId, zipProgress.totalFiles)
						startPredictedZipProgressTicks()
						publishPredictedZipProgress()
						return
					}

					zipProgressPredictor.setTotalFiles(zipProgress.totalFiles)
					zipProgressPredictor.beginZipping()
					zipProgressPredictor.recordProgress(zipProgress)
					useDownloadCentreStore
						.getState()
						.updateZipProgress(messageId, zipProgress)
					startPredictedZipProgressTicks()
					publishPredictedZipProgress()
				},
				abortController.signal
			)

			sendTrackedDownloadAnalytics(STARTED)

			stopPredictedZipProgressTicks()
			useDownloadCentreStore.getState().beginBulkFileDownload(messageId)

			const resumeBulkDownload = clientGetToggleFromCookie(
				'BulkDownloadJobResume'
			)

			let blob: Blob
			let downloadedFileName: string

			if (resumeBulkDownload) {
				const resumedDownload = await downloadBlobWithRangeResume({
					abortSignal: abortController.signal,
					fallbackFileName: fileName,
					onProgress: (percent) =>
						useDownloadCentreStore
							.getState()
							.updateDownloadProgress(
								messageId,
								mapBulkFileDownloadProgress(percent)
							),
					url,
				})
				blob = resumedDownload.blob
				downloadedFileName = resumedDownload.fileName
			} else {
				const fileResponse = await fetch(url, {
					headers: {
						Accept: 'application/octet-stream',
						'Content-Type': 'application/octet-stream',
					},
					method: 'GET',
					signal: abortController.signal,
				})

				if (!fileResponse.ok) {
					throw new Error('Download failed')
				}

				const download = await readDownloadResponseWithProgress(
					fileResponse,
					(percent) =>
						useDownloadCentreStore
							.getState()
							.updateDownloadProgress(
								messageId,
								mapBulkFileDownloadProgress(percent)
							),
					fileName,
					abortController.signal
				)
				blob = download.blob
				downloadedFileName = download.fileName
			}

			if (abortController.signal.aborted) {
				throw new DOMException('The operation was aborted.', 'AbortError')
			}

			await recordBulkDownloadCounts(
				selection,
				fileType,
				user.id,
				user.organisation.id,
				stemsDownloadTrack
			)

			createDownloadLink(blob, downloadedFileName)

			sendTrackedDownloadAnalytics(COMPLETED, formatDownloadSize(blob.size))

			useDownloadCentreStore
				.getState()
				.showSingleDownloadCompleted(messageId, downloadedFileName)

			recordDownloadLatency({
				downloadMode: 'bulk',
				durationMs: performance.now() - downloadStartedAt,
				fileType,
				outcome: 'success',
				pageActionName: DOWNLOAD_LATENCY_PAGE_ACTION,
				trackCount: latencyTrackCount,
			})
		} catch (error) {
			stopPredictedZipProgressTicks()

			if (isAbortError(error)) {
				return
			}

			logger(
				'Error downloading',
				error instanceof Error ? error : new Error(String(error)),
				{
					errorType: 'Download',
					downloadMode: 'bulk',
				}
			)
			useDownloadCentreStore
				.getState()
				.showDownloadFailed(messageId, zipFileName)
			toastMobileDownloadFailed()
		}
	}

	const downloadStems = async (
		track: Track,
		existingMessageId?: string
	): Promise<void> => {
		const selection = await getStemsDownloadSelection(track)

		await downloadTracks(selection, 'wav', {
			existingMessageId,
			stemsDownloadTrack: track,
		})
	}

	return {
		downloadStems,
		downloadTrack,
		downloadTracks,
		isAssociatedDownloadInProgress,
		isDownloading,
	}
}
